/// <amd-module name="Repos/Tfvc/Views/FilesHub/Components/FileContent" />
import * as React from "react";
import { IAnnotationBlock, IEditorSelection, IFileContentDetails, IFileViewerProps } from "Repos/Common/Contracts/Editor";
import { IGitItemContent } from "Repos/Files/Contracts";
import { IFilesHubFilterActionsService } from "Repos/FilesHubCommon/FilesHubFilterActionsService";
import * as Resources from "Repos/Tfvc/Views/FilesHub/Resources";
import { IReadyableReadonlyObservableArray } from "VSS/Core/Observable";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { getLinkWidgetRegistration } from "VSS/Features/MonacoWidgets/Registration";
import { PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { css, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Uri } from "VSS/Platform/Util/Url";
import { Card } from "VSSUI/Card";
import { HeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { getFilesHubViewService, registerFilesHubTab } from "../FilesHubViewService";

export interface IFileContentProps {
    showAnnotate?: boolean;
}

export class FileContent extends VssComponent<IFileContentProps> {
    public render() {
        return (
            <PerformanceScenarioContext.Consumer>
                {scenarioProps => {
                    scenarioProps.getScenario()!.addProperties({ editor: true });
                    const filesService = getFilesHubViewService(this.context.pageContext);
                    const pendingWorkComplete = scenarioProps.registerPendingWork("load-file-editor")!;
                    const selection = this.getSelectionFromQueryParams();
                    let annotationBlocks: IReadyableReadonlyObservableArray<IAnnotationBlock> | undefined;

                    if (this.props.showAnnotate) {
                        annotationBlocks = filesService.getSelectedItemAnnotationBlocks();
                    }

                    return (
                        <Observer content={filesService.getSelectedItemContent()}>
                            {(contentProps: { content: IGitItemContent | undefined }) => {
                                let contentDetails: IFileContentDetails | undefined;

                                if (contentProps.content) {
                                    contentDetails = contentProps.content.details;
                                    if (contentProps.content.editContext) {
                                        contentDetails = {
                                            content: contentProps.content.editContext.getContent(),
                                            contentTruncated: contentProps.content.details.contentTruncated,
                                            metadata: contentProps.content.details.metadata,
                                            objectId: contentProps.content.details.objectId,
                                            resourceUri: contentProps.content.details.resourceUri
                                        };
                                    }
                                }

                                const nonBinaryContent = contentDetails && !contentDetails.metadata.isBinary;

                                return (
                                    <Card
                                        className={css("no-padding", nonBinaryContent && "flex-grow v-scroll-hidden")}
                                        contentProps={{ className: "relative", contentPadding: false }}
                                    >
                                        {contentDetails ? (
                                            <div className={css("flex-row scroll-hidden", nonBinaryContent && "absolute-fill")}>
                                                <WrappedComponent<IFileViewerProps>
                                                    dependencies={["ms.vss-code-web.code-editor"]}
                                                    wrappedType="repos-file-viewer"
                                                    wrappedProps={{
                                                        annotationBlocks: annotationBlocks,
                                                        className: "flex-row flex-grow",
                                                        content: contentDetails,
                                                        editable: filesService.getEditMode(),
                                                        editorClassName: "repos-file-editor-background scroll-hidden",
                                                        editorWidgets: [getLinkWidgetRegistration(this.getCodeSelectionLinkUrl, this.context.pageContext, contentProps.content?.details.metadata.extension!)],
                                                        getCodeSelectionLinkUrl: this.getCodeSelectionLinkUrl,
                                                        getDownloadUrl: forceDownload =>
                                                            filesService.getFileDownloadUrl(
                                                                filesService.getSelectedItem().value.path,
                                                                undefined,
                                                                forceDownload
                                                            ),
                                                        initialSelectedText: selection,
                                                        onContentChanged: this.onContentChanged,
                                                        onLoaded: pendingWorkComplete.complete
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                                <div className="repos-file-editor-background flex-grow" />
                                            )}
                                    </Card>
                                );
                            }}
                        </Observer>
                    );
                }}
            </PerformanceScenarioContext.Consumer>
        );
    }

    private onContentChanged = (content: string): void => {
        const filesService = getFilesHubViewService(this.context.pageContext);
        const itemContent = filesService.getSelectedItemContent();

        if (itemContent.value && itemContent.value.editContext) {
            itemContent.value.editContext.setContent(content);
        }
    };

    private getCodeSelectionLinkUrl = (selection: IEditorSelection): string => {
        const filesService = getFilesHubViewService(this.context.pageContext);
        const url = new Uri(filesService.getItemUrl(filesService.getSelectedItem().value.path, { absoluteUrl: true }));
        url.addQueryParams({
            line: selection.startLineNumber,
            lineEnd: selection.endLineNumber,
            lineStartColumn: selection.startColumn,
            lineEndColumn: selection.endColumn,
            lineStyle: "plain",
            _a: "contents"
        });
        return url.absoluteUri;
    };

    private getSelectionFromQueryParams(): IEditorSelection | undefined {
        const pageHistoryService = this.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const state = pageHistoryService.getProjectedState();

        if (state.line && state.lineStartColumn) {
            // Construct a selection from the URL
            const selection: IEditorSelection = {
                startLineNumber: parseInt(state.line),
                endLineNumber: parseInt(state.lineEnd || state.line),
                startColumn: parseInt(state.lineStartColumn),
                endColumn: parseInt(state.lineEndColumn || state.lineStartColumn)
            };

            // Remove these values from the URL
            delete state.line;
            delete state.lineEnd;
            delete state.lineStartColumn;
            delete state.lineEndColumn;
            delete state.lineStyle;
            pageHistoryService.queueState(state, HistoryBehavior.replace);

            return selection;
        }

        return undefined;
    }
}

registerFilesHubTab({
    getTab: (pageContext, item, editMode) => {
        if (!item.isFolder && (!item.isInvalid || item.isNewItem)) {
            const actionService = pageContext.getService<IFilesHubFilterActionsService>("IFilesHubFilterActionsService");
            return {
                id: "contents",
                name: Resources.Contents,
                order: 10,
                render: () => <FileContent />,
                renderFilterBar: (inTabBar: boolean) => {
                    return inTabBar ? <HeaderCommandBar items={actionService.getActions()} /> : undefined;
                }
            };
        }
    },
    dependencies: ["ms.vss-code-web.code-editor"]
});
